import React, { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useCheckboxRecaptchaScripts } from './recaptcha-service';
import { captureRecaptchaException } from '../../../utilities/analytics/error-reporting';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';

import { RecaptchaError } from '../../../utilities/RecaptchaError';

type TrackingEventSuccessAction = 'loaded-recaptcha' | 'checkbox-completed';

type TrackingEventErrorAction = 'load-recaptcha-failed' | 'error-callback' | 'expired-callback';

type TrackingEventAction = TrackingEventSuccessAction | TrackingEventErrorAction;

type TrackingEventErrorType = 'recaptcha-window-error' | 'recaptcha-script-error';

export type CheckboxRecaptchaConfig = {
  sitekey: string;
  action: string;
  pageId: string;
  enabled: boolean;
};

export type GoogleRecaptcha = CheckboxRecaptchaConfig & {
  callback: (token: string) => void;
  ['expired-callback']?: () => void;
  ['error-callback']?: () => void;
};

export type CheckboxRecaptchaResult = {
  component: ReactNode;
  reset: () => void;
  token: string;
  error: Error | undefined;
  getResponse: () => string;
  enabled: boolean;
  isLoaded: boolean;
  isExpired: boolean;
};

export const useCheckboxRecaptcha = (config: CheckboxRecaptchaConfig): CheckboxRecaptchaResult => {
  const [instanceId, setInstanceId] = useState<number>();
  const [token, setToken] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [error, setError] = useState<Error>();
  const { sitekey, action, enabled, pageId } = config;
  const analyticsClient = useAnalyticsClient();
  const scriptLogic = useCheckboxRecaptchaScripts();

  const sendTrackingEvent = useCallback(
    (action: TrackingEventAction, errorType?: TrackingEventErrorType) => {
      analyticsClient.trackingEvent({
        page: pageId,
        action: action,
        subject: 'checkbox-recaptcha',
        ...(errorType && {
          attributes: {
            hasError: true,
            errorName: errorType,
          },
        }),
      });
    },
    [analyticsClient, pageId]
  );

  const handleVerify = useCallback(
    (newToken: string) => {
      setToken(newToken);
      setIsExpired(false);
      sendTrackingEvent('checkbox-completed');
    },
    [sendTrackingEvent]
  );

  const handleExpire = useCallback(() => {
    setToken('');
    setIsExpired(true);
    sendTrackingEvent('expired-callback', 'recaptcha-script-error');
  }, [sendTrackingEvent]);

  const handleError = useCallback(() => {
    const newError = RecaptchaError.fromMessage('Failed to load recaptcha script');
    setError(newError);
    captureRecaptchaException(newError, {}, 'no_token_checkbox');
    sendTrackingEvent('error-callback', 'recaptcha-script-error');
  }, [sendTrackingEvent]);

  const handleOnLoad = useCallback(() => {
    sendTrackingEvent('loaded-recaptcha');
    setIsLoaded(true);
  }, [sendTrackingEvent]);

  // This callback is used for rendering recaptcha into the ref associated HTML Element.
  // The recaptcha script is loaded, if not done already, and then rendered.
  const refCallback = useCallback(
    async (node: HTMLElement | null) => {
      if (!node) {
        return;
      }

      try {
        // Dynamically loads the recaptcha script when needed.
        await scriptLogic.injectRecaptchaScript();
        handleOnLoad();

        // Renders the recaptcha component into the given node.
        const newInstanceId = scriptLogic.getRecaptchaMethods().render(node, {
          sitekey,
          action,
          callback: handleVerify,
          'expired-callback': handleExpire,
          'error-callback': handleError,
        });
        setInstanceId(newInstanceId);
      } catch (error) {
        const newError = RecaptchaError.fromMessage('Failed to load recaptcha script');
        setError(newError);
        captureRecaptchaException(newError, {}, 'no_token_checkbox');
        sendTrackingEvent('load-recaptcha-failed', 'recaptcha-window-error');
      }
    },
    [
      scriptLogic,
      handleOnLoad,
      sitekey,
      action,
      handleVerify,
      handleExpire,
      handleError,
      sendTrackingEvent,
    ]
  );

  const component = useMemo(
    () => (enabled ? <div id="checkbox-recaptcha-hook" ref={refCallback} /> : null),
    [enabled, refCallback]
  );

  const reset = useCallback(() => {
    if (instanceId === undefined) {
      return;
    }
    scriptLogic.getRecaptchaMethods().reset(instanceId);
    setToken('');
    setIsLoaded(false);
    setIsExpired(false);
  }, [instanceId, scriptLogic]);

  const getResponse = useCallback(() => {
    return instanceId !== undefined
      ? scriptLogic.getRecaptchaMethods().getResponse(instanceId)
      : '';
  }, [instanceId, scriptLogic]);

  return {
    component,
    reset,
    token,
    error,
    getResponse,
    enabled,
    isLoaded,
    isExpired,
  };
};
